import qs from 'qs';
import { BehaviorSubject, filter } from 'rxjs';
/**
 * SourSocketClient
 * @example const client = new SourSocketClient('wss://example.com');
 * @example const socket = client.connect('/endpoint');
 * @example socket.send({ type: 'test', data: 'test data' });
 * @example socket.receive().subscribe((data) => console.log(data));
 * @example socket.unsubscribe();
 * @example client.killAllSockets();
 */
class SourSocketClient {
    config;
    baseURL;
    token = null;
    allSockets = new Map();
    retryCount = 0;
    maxRetries;
    initialRetryDelay;
    constructor(baseURl, config = {
        debug: false,
        maxRetries: 5,
        initialRetryDelay: 1000,
        keepAliveOnBlur: false,
    }) {
        this.config = config;
        // if the url starts with http:// or https://, replace it with ws:// or wss://
        if (baseURl.startsWith('http://')) {
            baseURl = 'ws://' + baseURl.substring(7);
        }
        else if (baseURl.startsWith('https://')) {
            baseURl = 'wss://' + baseURl.substring(8);
        }
        this.baseURL = baseURl;
        // make sure the browser supports WebSocket
        if (!window.WebSocket) {
            throw new Error('WebSocket is not supported. Please use a modern browser.');
        }
        this.maxRetries = config.maxRetries ?? 5;
        this.initialRetryDelay = config.initialRetryDelay ?? 1000;
    }
    /**
     * Build a URL with the correct query params.
     * @param url The endpoint to connect to. The base URL is set in the constructor so you only need to pass the endpoint.
     * @param options The options to use when building the URL.
     * @returns The full URL.
     * @example const url = client.buildURL('/endpoint', { params: { test: 'test' } });
     * console.log(url); // https://example.com/endpoint?test=test
     */
    buildURL(url, options = {}) {
        const baseURL = options.baseURL || this.baseURL;
        const combinedParams = {
            ...options.params,
        };
        if (this.token) {
            combinedParams.authorization = this.token?.split(' ')[1]; // we don't want the Bearer part
        }
        return baseURL + url + (Object.keys(combinedParams).length > 0 ? '?' + qs.stringify(combinedParams) : '');
    }
    async retryConnection(finalURL, subject, makeClient, error) {
        if (this.retryCount >= this.maxRetries) {
            if (this.config.debug)
                console.log('Maximum retry attempts reached');
            const timeoutError = {
                originalError: error,
                message: 'WebSocket connection timed out after maximum retry attempts',
                retryAttempts: this.maxRetries,
                type: 'WEBSOCKET_TIMEOUT',
            };
            console.error('WebSocket timeout error:', timeoutError);
            subject.error(timeoutError);
            return;
        }
        const delay = this.initialRetryDelay * Math.pow(2, this.retryCount);
        if (this.config.debug)
            console.log(`Retrying connection in ${delay}ms (attempt ${this.retryCount + 1}/${this.maxRetries})`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (this.config.debug)
            console.log('Attempting to reconnect...');
        this.retryCount++;
        const newClient = makeClient();
        // Update the client reference in the closure
        if (this.allSockets.has(finalURL)) {
            const socketInfo = this.allSockets.get(finalURL);
            if (socketInfo) {
                socketInfo.socket.client = newClient;
            }
        }
    }
    /**
     * Connect to a WebSocket server.
     * The first type parameter is the type of data you expect to receive from the server.
     * The second type parameter is the type of data you want to send to the server.
     * @param url The endpoint to connect to. The base URL is set in the constructor so you only need to pass the endpoint.
     * @returns SourSocket
     */
    connect(url, options = {}) {
        // needs to be a behavior subject so we can emit the last value when a new subscriber connects
        const subject = new BehaviorSubject(null);
        // Explicitly check if the route-level option is defined before falling back to global config
        const keepAliveOnBlur = options.keepAliveOnBlur !== undefined ? options.keepAliveOnBlur : this.config.keepAliveOnBlur;
        const finalURL = this.buildURL(url, options);
        if (this.allSockets.has(finalURL)) {
            const refSocket = this.allSockets.get(finalURL);
            if (refSocket?.closeTimeout) {
                clearTimeout(refSocket.closeTimeout);
            }
            return refSocket?.socket;
        }
        const makeClient = () => {
            const client = new WebSocket(finalURL);
            // set encoding to UTF-8
            client.binaryType = 'arraybuffer';
            client.addEventListener('open', (event) => {
                this.retryCount = 0; // Reset retry count on successful connection
                if (this.config.onOpen) {
                    this.config.onOpen(event);
                }
                if (this.config.debug)
                    console.log('WebSocket connection established');
            });
            client.addEventListener('close', (closeEvent) => {
                if (this.config.onClose) {
                    this.config.onClose(closeEvent);
                }
                if (this.config.debug)
                    console.log('WebSocket connection closed');
            });
            client.addEventListener('error', (error) => {
                if (this.config.onSocketError) {
                    this.config.onSocketError(error);
                }
                // Only retry if the socket is actually closed
                if (client.readyState === WebSocket.CLOSED) {
                    this.retryConnection(finalURL, subject, makeClient, error);
                }
            });
            client.addEventListener('message', (event) => {
                const data = JSON.parse(event.data);
                if (this.config.debug)
                    console.log('WebSocket message received:', data);
                if (data.type === 'ERROR') {
                    if (this.config.onError) {
                        this.config.onError(data.error);
                    }
                    subject.error({
                        message: data.error,
                        additionalInfo: data.additionalInfo,
                    });
                    return;
                }
                if (data.type === 'PING') {
                    const pongMessage = {
                        type: 'PONG',
                        timestamp: data.timestamp,
                    };
                    client.send(JSON.stringify(pongMessage));
                    // we don't want to emit the ping message
                    return;
                }
                subject.next(data.payload);
            });
            return client;
        };
        let client = makeClient();
        const connectionPromise = new Promise((resolve, reject) => {
            client.addEventListener('open', () => {
                resolve();
            });
            client.addEventListener('error', (error) => {
                reject(error);
            });
        });
        // Use the computed keepAliveOnBlur value here instead of checking both values
        if (!keepAliveOnBlur) {
            // when the window enters the background, set a 5 second timeout to close the socket
            // this is to prevent the socket from staying open when the user leaves the page
            let timeout;
            window.addEventListener('blur', () => {
                if (this.config.debug)
                    console.log('Tab Blur. Socket will close in 5 seconds...');
                timeout = window.setTimeout(() => {
                    if (this.config.debug)
                        console.log('Tab Blur. Closing Socket...');
                    client.close();
                }, 5000);
            });
            // when the window comes back into focus, clear the timeout
            // and reconnect the socket if it's not already open
            window.addEventListener('focus', () => {
                if (timeout)
                    clearTimeout(timeout);
                if (this.config.debug)
                    console.log('Tab Focus. Reconnecting Socket...');
                if (this.allSockets.has(finalURL) && client.readyState !== WebSocket.OPEN && client.readyState !== WebSocket.CONNECTING) {
                    client = makeClient();
                }
            });
        }
        const closeSocket = () => {
            client.close();
            subject.complete();
            this.allSockets.delete(finalURL);
        };
        const socket = {
            send: (data) => {
                if (client.readyState === WebSocket.OPEN) {
                    const message = {
                        type: 'MESSAGE',
                        timestamp: new Date().toISOString(),
                        payload: data,
                    };
                    client.send(JSON.stringify(message));
                }
                else {
                    throw new Error('WebSocket connection is not open');
                }
            },
            // no great way to make TS happy that we are filtering out the null values.
            receive: subject.asObservable().pipe(filter((data) => data !== null)),
            unsubscribe: () => {
                // set a timeout to close the socket if it's not closed within 5 seconds
                const closeTimeout = window.setTimeout(closeSocket, 1000);
                this.allSockets.set(finalURL, {
                    socket: socket,
                    closeTimeout,
                });
            },
            onConnect: connectionPromise,
            kill: closeSocket,
        };
        this.allSockets.set(finalURL, {
            socket: socket,
        });
        return socket;
    }
    /**
     * Send a message to a specific socket by its URL.
     * @param url The endpoint URL that was used to connect to the socket
     * @param data The data to send to the server
     * @param options Optional connection options if the socket needs to be created
     * @returns A promise that resolves when the message is sent
     * @throws Error if the socket is not found and cannot be created, or if the socket is not open
     */
    async sendMessage(url, data, options = {}) {
        const finalURL = this.buildURL(url, options);
        let socketInfo = this.allSockets.get(finalURL);
        // If socket doesn't exist, try to create it
        if (!socketInfo) {
            if (this.config.debug)
                console.log(`Socket for ${finalURL} not found, creating new connection`);
            const socket = this.connect(url, options);
            // Wait for connection to establish
            await socket.onConnect;
            socketInfo = this.allSockets.get(finalURL);
            if (!socketInfo) {
                throw new Error(`Failed to create socket connection to ${finalURL}`);
            }
        }
        const socket = socketInfo.socket;
        try {
            socket.send(data);
            return Promise.resolve();
        }
        catch (error) {
            if (this.config.debug)
                console.error(`Error sending message to ${finalURL}:`, error);
            return Promise.reject(error);
        }
    }
    /**
     * Kills all the active sockets.
     */
    kill() {
        Object.keys(this.allSockets).forEach((key) => {
            this.allSockets.get(key)?.socket?.kill();
        });
    }
    /**
     * Set the token to be sent to the server.
     * @param token The token to send to the server.
     */
    setToken(token) {
        this.token = token;
    }
}
export { SourSocketClient };
//# sourceMappingURL=index.js.map